import React, {useEffect, useState} from 'react';
import {CustomCard} from "../../../../components/UI/AntCustom/CustomCard";
import {PageWrapper} from "../../../../components/UI/PageWrapper/PageWrapper";
import {useAppDispatch, useAppSelector} from "../../../../store/store";
import {
  formatDate,
} from "../../../../utils/helpers/dateHelpers/dateHelpers";
import {
  clearScheduleResult,
  getSchedule,
} from "../../../../store/reducers/scheduleReducer/scheduleReducer";
import {ScheduleCreateModal} from "./components/ScheduleCreateModal";
import {ScheduleEditModal} from "./components/ScheduleEditModal";
import {BookingApiPlaneListRequest, PageModel, ScheduleType} from "../../../../proto/generated/api_entities_pb";
import {PlaneStatus} from "../../../../utils/constans/statuses/planeStatuses";
import {useSearchParams} from "react-router-dom";
import {IPlaneEvent} from "../../../../store/reducers/scheduleReducer/scheduleReducerTypes";
import {InitialPage} from "../../../../utils/constans/pagination/pagination";
import {DebounceSelect} from "../../../../components/UI/AntCustom/DebounceSelect";
import {fetchOwnPlanes, getInitialOwnPlaneForSelect} from "../../../../utils/helpers/selectDataFetchers/fetchOwnPlanes";
import {
  getRecentUTCMondayWithWeekOffset,
  getUTCDateWithDayOffset
} from "../../../../utils/helpers/dateHelpers/calculateArrivalDate";
import {IPlaneShort} from "../../../../store/reducers/fleetReducer/fleetReducerTypes";
import isNumeric from "antd/es/_util/isNumeric";
import {CustomButton} from "../../../../components/UI/AntCustom/CustomButton";
import styled from "styled-components";
import {CustomFieldInput} from "../../../../components/UI/AntCustom/CustomFieldInput";
import {Switch} from 'antd';
import {DisplayAirport, DisplayRoute} from "../../../../utils/helpers/textDisplayHelpers/DisplayAirport";
import {defineMessages, useIntl} from "react-intl";
import {CommonDetailsMessages} from "../../../../intl/commonMessages/CommonDetailsMessages";
import {CommonFormMessages} from "../../../../intl/commonMessages/CommonFormMessages";
import {FullCalendarWrapper} from "./components/FullCalendarWrapper";
import {API} from "../../../../API/API";
import {CustomTabs} from "../../../../components/UI/AntCustom/CustomTabs";
import CalendarIcon from "../../../../media/icons/calendar.svg?react";
import CalendarBlackIcon from "../../../../media/icons/calendar_black.svg?react";
import TimelineIcon from "../../../../media/icons/timeline.svg?react";
import TimelineBlackIcon from "../../../../media/icons/timeline_black.svg?react";
import MapIcon from "../../../../media/icons/map.svg?react";
import MapBlackIcon from "../../../../media/icons/map_black.svg?react";
import {useIsMobile} from "../../../../hooks/useWindowSize";

const ControlsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const FiltersRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const SettingsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
`;

const WeekToggleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 14px;
  color: #666666;
`;


const TabContentPlaceholder = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 420px;
  color: #6c6c6c;
  font-size: 16px;
`;

const TabLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

const TabIconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const TABS = {
  calendar: "calendar",
  timeline: "timeline",
  map: "map",
} as const;

type TabKey = typeof TABS[keyof typeof TABS];


const SchedulePageMessages = defineMessages({
  title: {
    id: "schedulePage.title",
    defaultMessage: "Aircraft fleet schedule",
  },
  chosenDay: {
    id: "schedulePage.chosenDay",
    defaultMessage: "Chosen day",
  },
  noEvents: {
    id: "schedulePage.noEvents",
    defaultMessage: "No events found",
  },
  addEvent: {
    id: "schedulePage.addEvent",
    defaultMessage: "Add to schedule",
  },
  weekStartsSunday: {
    id: "schedulePage.weekStartsSunday",
    defaultMessage: "Week starts on Sunday",
  },
  calendarTab: {
    id: "schedulePage.tabs.calendar",
    defaultMessage: "Calendar",
  },
  timelineTab: {
    id: "schedulePage.tabs.timeline",
    defaultMessage: "Timeline",
  },
  mapTab: {
    id: "schedulePage.tabs.map",
    defaultMessage: "Map",
  },
  mapPlaceholder: {
    id: "schedulePage.mapPlaceholder",
    defaultMessage: "Map view will be available soon",
  },
  showOnlyPublic: {
    id: "schedulePage.showOnlyPublic",
    defaultMessage: "Show only public events",
  },
})

export const SchedulePageNew = () => {
  const dispatch = useAppDispatch();
  const intl = useIntl();
  const isMobile = useIsMobile();
  const {items, isLoading, totalCount} = useAppSelector(state => state.schedule.itemsObject);

  const [searchParams] = useSearchParams();
  const planeIdFromParams = searchParams.get('planeId');
  const planeRegNumberFromParams = searchParams.get('planeRegNumber');
  const defaultSelectedPlane: IPlaneShort | undefined = isNumeric(planeIdFromParams) && planeRegNumberFromParams ? {
    id: Number(planeIdFromParams),
    registrationNumber: planeRegNumberFromParams,
  } : undefined;

  const [selectedPlaneIds, setSelectedPlaneIds] = useState<number[]>(defaultSelectedPlane ? [defaultSelectedPlane.id] : []);
  const [allPlanes, setAllPlanes] = useState<IPlaneShort[]>([]);

  const [isCreateModalVisible, setIsCreateModalVisible] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const [lastSelectedEvent, setLastSelectedEvent] = useState<IPlaneEvent | undefined>(undefined);
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [calendarView, setCalendarView] = useState<string>('dayGridMonth');
  const [weekStartsSunday, setWeekStartsSunday] = useState<boolean>(false);
  const [activeTabKey, setActiveTabKey] = useState<TabKey>(TABS.calendar);
  const [showOnlyPublic, setShowOnlyPublic] = useState<boolean>(false);
  const [visibleDateRange, setVisibleDateRange] = useState<{start: Date, end: Date} | null>(null);

  useEffect(() => {
    let isMounted = true;

    const loadPlanes = async () => {
      try {
        const request = new BookingApiPlaneListRequest()
          .setQuery("")
          .setPagemodel(
            new PageModel()
              .setPage(InitialPage)
              .setLimit(1000) // Большой лимит, чтобы получить все самолёты для фильтрации
          );
        const response = await API.getPlaneList(request);

        // Фильтруем только одобренные самолёты (PlaneStatus.APPROVED = 3)
        const planes = response.getPlanesList()
          .filter(plane => plane.getPlanestatusid() === PlaneStatus.APPROVED)
          .map((plane) => ({
            id: plane.getPlaneid(),
            registrationNumber: plane.getRegistrationnumber(),
          }));

        if (isMounted) {
          setAllPlanes(planes);
        }
      } catch (error) {
        console.error('Failed to load plane list', error);
      }
    };

    loadPlanes();

    return () => {
      isMounted = false;
    };
  }, []);

  // Загружаем данные при изменении выбранных самолетов или видимого диапазона дат
  useEffect(() => {
    if (!visibleDateRange) {
      // Если видимый диапазон еще не установлен, используем текущую дату для вычисления недели
      const startOfWeek = new Date(currentDate);
      startOfWeek.setDate(currentDate.getDate() - currentDate.getDay() + 1); // Понедельник
      startOfWeek.setHours(0, 0, 0, 0);
      
      const endOfWeek = new Date(startOfWeek);
      endOfWeek.setDate(startOfWeek.getDate() + 6); // Воскресенье
      endOfWeek.setHours(23, 59, 59, 999);

      dispatch(getSchedule({
        page: InitialPage,
        dateFrom: startOfWeek,
        dateTo: endOfWeek,
        planeIds: selectedPlaneIds,
        isPublic: showOnlyPublic ? true : undefined,
      }))
    } else {
      // Используем видимый диапазон дат из календаря
      dispatch(getSchedule({
        page: InitialPage,
        dateFrom: visibleDateRange.start,
        dateTo: visibleDateRange.end,
        planeIds: selectedPlaneIds,
        isPublic: showOnlyPublic ? true : undefined,
      }))
    }

    return () => {
      dispatch(clearScheduleResult())
    }
  }, [selectedPlaneIds, currentDate, showOnlyPublic, visibleDateRange])

  // Обработчики для FullCalendar
  const handleEventClick = (event: IPlaneEvent) => {
    setLastSelectedEvent(event);
    setIsEditModalVisible(true);
  };

  const handleDateSelect = (start: Date, end: Date) => {
    // Можно добавить логику для создания события на выбранную дату
    console.log('Selected date range:', start, end);
  };

  const handleCalendarDateChange = (date: Date) => {
    setCurrentDate(date);
  };

  const handleDatesChange = (start: Date, end: Date) => {
    setVisibleDateRange({ start, end });
  };

  const renderCalendarContent = () => (
    <>
      <FullCalendarWrapper
        events={items}
        onEventClick={handleEventClick}
        onDateSelect={handleDateSelect}
        onDatesChange={handleDatesChange}
        height={600}
        initialView="dayGridMonth"
        initialDate={currentDate}
        firstDayOfWeek={weekStartsSunday ? 0 : 1}
        headerRightContent={
          <WeekToggleWrapper>
            <Switch
              checked={weekStartsSunday}
              onChange={setWeekStartsSunday}
              size="small"
            />
            <span>
              {intl.formatMessage(SchedulePageMessages.weekStartsSunday)}
            </span>
          </WeekToggleWrapper>
        }
      />
    </>
  );

  const renderCalendarTimelineContent = () => (
      <>
        <div style={{ display: 'flex', gap: 16 }}>
          <div style={{ width: 140, paddingTop: 117, display: 'flex', flexDirection: 'column' }}>
            {allPlanes.length > 0 ? (
              allPlanes.map((plane) => (
                <div key={plane.id} style={{
                  height: 102,
                  display: 'flex',
                  padding: '0 12px',
                  fontSize: 12,
                  fontWeight: 600,
                  color: '#343A40',
                  borderRadius: 4,
                  background: 'transparent',
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                }}>
                  {plane.registrationNumber}
                </div>
              ))
            ) : (
              <div style={{
                height: 102,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '0 12px',
                fontSize: 12,
                fontWeight: 600,
                color: '#bfbfbf',
                borderRadius: 4,
              }}>
                —
              </div>
            )}
          </div>
          <div style={{ flex: 1 }}>
            <FullCalendarWrapper
              events={items}
              onEventClick={handleEventClick}
              onDateSelect={handleDateSelect}
              onDatesChange={handleDatesChange}
              height={600}
              initialView="timelineWeek"
              initialDate={currentDate}
              firstDayOfWeek={weekStartsSunday ? 0 : 1}
              availableViewModes={['week']}
              initialViewMode="week"
              planeList={allPlanes}
              headerRightContent={
                <WeekToggleWrapper>
                  <Switch
                    checked={weekStartsSunday}
                    onChange={setWeekStartsSunday}
                    size="small"
                  />
                  <span>
                    {intl.formatMessage(SchedulePageMessages.weekStartsSunday)}
                  </span>
                </WeekToggleWrapper>
              }
            />
          </div>
        </div>
      </>
  );

  const addEventButton = (
    <CustomButton
      heightSize={isMobile ? 's' : 'm'}
      type={'primary'}
      onClick={() => setIsCreateModalVisible(true)}
    >
      + {intl.formatMessage(SchedulePageMessages.addEvent)}
    </CustomButton>
  );

  return (
    <PageWrapper>
      <CustomCard width={"max"} title={intl.formatMessage(SchedulePageMessages.title)} isContainerCard={true}
                  extra={addEventButton} titlePosition={'space-between'}>
        <ControlsWrapper>
          {/*<FiltersRow>*/}
          {/*  <CustomFieldInput>*/}
          {/*    <DebounceSelect*/}
          {/*      mode={"multiple"}*/}
          {/*      fetchOptions={fetchOwnPlanes}*/}
          {/*      placeholder={intl.formatMessage(CommonFormMessages.planePlaceholder)}*/}
          {/*      showSearch={true}*/}
          {/*      allowClear={true}*/}
          {/*      style={{width: 261}}*/}
          {/*      defaultValue={defaultSelectedPlane ? [getInitialOwnPlaneForSelect(defaultSelectedPlane)] : []}*/}
          {/*      onChange={(values) => {*/}
          {/*        setSelectedPlaneIds(values as unknown as number[])*/}
          {/*      }}*/}
          {/*    />*/}
          {/*  </CustomFieldInput>*/}
          {/*</FiltersRow>*/}

          <SettingsRow>
            <div style={{display: 'flex', gap: '16px', alignItems: 'center'}}>
              <WeekToggleWrapper>
                <span>
                  {intl.formatMessage(SchedulePageMessages.showOnlyPublic)}
                </span>
                <Switch
                  checked={showOnlyPublic}
                  onChange={setShowOnlyPublic}
                  size="small"
                />
              </WeekToggleWrapper>
            </div>
          </SettingsRow>
        </ControlsWrapper>

        <CustomTabs activeKey={activeTabKey} onChange={(key) => setActiveTabKey(key as TabKey)} destroyInactiveTabPane
          items={[
            {
              key: TABS.calendar,
              label: (
                <TabLabel>
                  <TabIconWrapper>
                    {activeTabKey === TABS.calendar ? <CalendarIcon /> : <CalendarBlackIcon />}
                  </TabIconWrapper>
                  {intl.formatMessage(SchedulePageMessages.calendarTab)}
                </TabLabel>
              ),
              children: renderCalendarContent(),
            },
            {
              key: TABS.timeline,
              label: (
                <TabLabel>
                  <TabIconWrapper>
                    {activeTabKey === TABS.timeline ? <TimelineIcon /> : <TimelineBlackIcon />}
                  </TabIconWrapper>
                  {intl.formatMessage(SchedulePageMessages.timelineTab)}
                </TabLabel>
              ),
              children: renderCalendarTimelineContent(),
            },
            {
              key: TABS.map,
              label: (
                <TabLabel>
                  <TabIconWrapper>
                    {activeTabKey === TABS.map ? <MapIcon /> : <MapBlackIcon />}
                  </TabIconWrapper>
                  {intl.formatMessage(SchedulePageMessages.mapTab)}
                </TabLabel>
              ),
              children: (
                <TabContentPlaceholder>
                  {intl.formatMessage(SchedulePageMessages.mapPlaceholder)}
                </TabContentPlaceholder>
              ),
            },
          ]}
        />
      </CustomCard>

      {isCreateModalVisible &&
        <ScheduleCreateModal isModalVisible={isCreateModalVisible} setIsModalVisible={setIsCreateModalVisible}/>}
      {isEditModalVisible && <ScheduleEditModal planeEvent={lastSelectedEvent!} isModalVisible={isEditModalVisible}
                                                setIsModalVisible={setIsEditModalVisible}/>}
    </PageWrapper>
  );
};
